import * as React from 'react';
import { createPortal } from 'react-dom';
import { FiTrendingUp } from 'react-icons/fi';
import Card from 'components/Card';
import SectionTitle from 'components/SectionTitle';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import BarChartTrends from './BarChartTrends';
import BarChartExpiryTrends from './BarChartExpiryTrends';
import BarChartStrikeTrends from './BarChartStrikeTrends';
import { BarChartSectionProps, ValueType } from './props';
import { buildTradesByDate, buildTradesByExpiry, buildTradesByStrike } from './buildBarChartGroups';
import { FeedTypeToggle } from './styles';

type TrendMetric = 'premium' | 'volume';
type TrendScope = 'tradeDate' | 'expiry' | 'strike';

enum GraphType {
  Trend,
  ExpiryTrend,
  StrikeTrend,
}

type GraphOption = {
  name: string;
  label: string;
  prefix: string;
  formatter: (num: number) => string | number;
  graphType: GraphType;
  metric: TrendMetric;
  scope: TrendScope;
  dataKey?: TrendMetric;
  dataType?: TrendMetric;
  valueType: ValueType;
};

const TREND_SCOPE_ORDER: TrendScope[] = ['tradeDate', 'expiry', 'strike'];
const TREND_METRIC_ORDER: TrendMetric[] = ['premium', 'volume'];

const TREND_SCOPE_LABELS: Record<TrendScope, string> = {
  tradeDate: 'Trade Date',
  expiry: 'Expiry',
  strike: 'Strike',
};

const TREND_SCOPE_CONTROL_LABELS: Record<TrendScope, string> = {
  tradeDate: 'Date',
  expiry: 'Expiry',
  strike: 'Strike',
};

const VALUE_TYPE_LABELS: Record<ValueType, string> = {
  [ValueType.Total]: 'Total',
  [ValueType.Net]: 'Net',
};

const GRAPH_FILTERS: GraphOption[] = [
  {
    name: 'Net Premium by Trade Date',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    metric: 'premium',
    scope: 'tradeDate',
    dataKey: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Premium by Trade Date',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    metric: 'premium',
    scope: 'tradeDate',
    dataKey: 'premium' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Net Volume by Trade Date',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    metric: 'volume',
    scope: 'tradeDate',
    dataKey: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Volume by Trade Date',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    metric: 'volume',
    scope: 'tradeDate',
    dataKey: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Net Volume by Expiry',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    metric: 'volume',
    scope: 'expiry',
    dataType: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Premium by Expiry',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    metric: 'premium',
    scope: 'expiry',
    dataType: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Volume by Strike',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    metric: 'volume',
    scope: 'strike',
    dataType: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Premium by Strike',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    metric: 'premium',
    scope: 'strike',
    dataType: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Volume by Expiry',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    metric: 'volume',
    scope: 'expiry',
    dataType: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Premium by Expiry',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    metric: 'premium',
    scope: 'expiry',
    dataType: 'premium' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Volume by Strike',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    metric: 'volume',
    scope: 'strike',
    dataType: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Premium by Strike',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    metric: 'premium',
    scope: 'strike',
    dataType: 'premium' as const,
    valueType: ValueType.Total,
  },
];

const getTrendGraphIndex = (scope: TrendScope, metric: TrendMetric, valueType: ValueType) =>
  GRAPH_FILTERS.findIndex(
    (option) =>
      option.scope === scope && option.metric === metric && option.valueType === valueType,
  );

const getTrendSelectorLabel = (option: GraphOption) =>
  `${option.label} / ${TREND_SCOPE_CONTROL_LABELS[option.scope]}`;

const TrendSelectButton: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClick: () => void;
  }>
> = ({ children, isOpen, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex h-[22px] w-[116px] items-center rounded-sm border px-2 text-[10px] font-medium leading-none transition-colors sm:w-[132px] ${
      isOpen
        ? 'border-button-color bg-button-color text-white'
        : 'border-palette-border bg-palette-black-1 text-gray-200 hover:border-button-color hover:text-white'
    }`}
  >
    <span className="min-w-0 flex-1 truncate">{children}</span>
    <span className="ml-1 text-[8px]">▾</span>
  </button>
);

const BarChartSection: React.FC<React.PropsWithChildren<BarChartSectionProps>> = ({
  activeTicker,
  trades,
  metricMode,
  showMetricModeControls = true,
}) => {
  const [activeGraph, setActiveGraph] = React.useState(() => {
    const initialValueType = metricMode === 'total' ? ValueType.Total : ValueType.Net;
    const initialGraph = getTrendGraphIndex('tradeDate', 'premium', initialValueType);

    return initialGraph >= 0 ? initialGraph : 0;
  });
  const [isTrendMenuOpen, setIsTrendMenuOpen] = React.useState(false);
  const [trendMenuPosition, setTrendMenuPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const trendWidgetRef = React.useRef<HTMLDivElement>(null);
  const trendButtonRef = React.useRef<HTMLDivElement>(null);
  const trendMenuRef = React.useRef<HTMLDivElement>(null);
  const activeGraphOption = GRAPH_FILTERS[activeGraph] || GRAPH_FILTERS[0];
  const tradesByDate = React.useMemo(() => buildTradesByDate(trades), [trades]);
  const tradesByExpiry = React.useMemo(() => buildTradesByExpiry(trades), [trades]);
  const tradesByStrike = React.useMemo(() => buildTradesByStrike(trades), [trades]);

  const updateTrendMenuPosition = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const triggerRect = trendButtonRef.current?.getBoundingClientRect();
    if (!triggerRect) {
      return;
    }

    const widgetRect = trendWidgetRef.current?.getBoundingClientRect();
    const boundaryLeft = Math.max(16, (widgetRect?.left ?? 16) + 8);
    const boundaryRight = Math.min(
      window.innerWidth - 16,
      (widgetRect?.right ?? window.innerWidth - 16) - 8,
    );
    const boundaryWidth = Math.max(160, boundaryRight - boundaryLeft);
    const width = Math.min(420, boundaryWidth);
    const preferredLeft = triggerRect.left + triggerRect.width / 2 - width / 2;
    const left = Math.max(boundaryLeft, Math.min(preferredLeft, boundaryRight - width));
    const spaceBelow = window.innerHeight - triggerRect.bottom - 16;
    const spaceAbove = triggerRect.top - 16;
    const shouldOpenAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(320, Math.max(120, shouldOpenAbove ? spaceAbove : spaceBelow));
    const top = shouldOpenAbove
      ? Math.max(16, triggerRect.top - maxHeight - 6)
      : triggerRect.bottom + 6;

    setTrendMenuPosition({
      top,
      left,
      width,
      maxHeight,
    });
  }, []);

  React.useEffect(() => {
    if (!isTrendMenuOpen) {
      return;
    }

    updateTrendMenuPosition();

    window.addEventListener('resize', updateTrendMenuPosition);
    window.addEventListener('scroll', updateTrendMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateTrendMenuPosition);
      window.removeEventListener('scroll', updateTrendMenuPosition, true);
    };
  }, [isTrendMenuOpen, updateTrendMenuPosition]);

  React.useEffect(() => {
    if (!isTrendMenuOpen) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (trendButtonRef.current?.contains(target) || trendMenuRef.current?.contains(target)) {
        return;
      }

      setIsTrendMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTrendMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTrendMenuOpen]);

  React.useEffect(() => {
    if (!metricMode) {
      return;
    }

    const nextValueType = metricMode === 'total' ? ValueType.Total : ValueType.Net;

    setActiveGraph((currentGraph) => {
      const currentOption = GRAPH_FILTERS[currentGraph] || GRAPH_FILTERS[0];
      if (currentOption.valueType === nextValueType) {
        return currentGraph;
      }

      const nextGraph = getTrendGraphIndex(
        currentOption.scope,
        currentOption.metric,
        nextValueType,
      );
      return nextGraph >= 0 ? nextGraph : currentGraph;
    });
  }, [metricMode]);

  const handleValueTypeChange = (valueType: ValueType) => {
    const nextGraph = getTrendGraphIndex(
      activeGraphOption.scope,
      activeGraphOption.metric,
      valueType,
    );

    if (nextGraph >= 0) {
      setActiveGraph(nextGraph);
    }
  };

  const trendNavigationGroups = TREND_SCOPE_ORDER.map((scope) => ({
    scope,
    options: TREND_METRIC_ORDER.map((metric) => {
      const optionIndex = getTrendGraphIndex(scope, metric, activeGraphOption.valueType);

      return optionIndex >= 0
        ? {
            option: GRAPH_FILTERS[optionIndex],
            optionIndex,
          }
        : null;
    }).filter((item): item is { option: GraphOption; optionIndex: number } => item !== null),
  })).filter((group) => group.options.length > 0);

  const trendMenu =
    isTrendMenuOpen && trendMenuPosition && typeof document !== 'undefined'
      ? (createPortal(
          <div
            ref={trendMenuRef}
            className="fixed rounded-sm border border-palette-border bg-palette-black-2 p-2 shadow-2xl"
            style={{
              top: trendMenuPosition.top,
              left: trendMenuPosition.left,
              width: trendMenuPosition.width,
              maxHeight: trendMenuPosition.maxHeight,
              overflowY: 'auto',
              zIndex: 9999,
            }}
          >
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))' }}
            >
              {trendNavigationGroups.map((group) => (
                <div key={group.scope}>
                  <div className="mb-1 text-[9px] font-semibold uppercase leading-none text-gray-500">
                    {TREND_SCOPE_LABELS[group.scope]}
                  </div>
                  <div className="space-y-1">
                    {group.options.map(({ option, optionIndex }) => {
                      const isActive = activeGraph === optionIndex;

                      return (
                        <button
                          key={option.name}
                          type="button"
                          onClick={() => {
                            setActiveGraph(optionIndex);
                            setIsTrendMenuOpen(false);
                          }}
                          className={`block h-[24px] w-full rounded-sm border px-2 text-left text-[10px] font-medium leading-none transition-colors ${
                            isActive
                              ? 'border-button-color bg-button-color text-white'
                              : 'border-palette-border bg-palette-black-1 text-gray-300 hover:border-button-color hover:text-white'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        ) as unknown as React.ReactNode)
      : null;

  return (
    <div ref={trendWidgetRef} className="h-full w-full">
      <Card className="flex h-full w-full flex-col" isDark>
        <SectionTitle
          Icon={FiTrendingUp}
          title={`${activeTicker || 'Ticker'} Trends`}
          className="mb-1 shrink-0 flex-wrap gap-y-1"
          right={
            <div className="relative ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-1">
              {showMetricModeControls && (
                <div className="flex shrink-0 items-center">
                  {[ValueType.Total, ValueType.Net].map((valueType) => (
                    <FeedTypeToggle
                      key={valueType}
                      isActive={activeGraphOption.valueType === valueType}
                      onClick={() => handleValueTypeChange(valueType)}
                    >
                      {VALUE_TYPE_LABELS[valueType]}
                    </FeedTypeToggle>
                  ))}
                </div>
              )}
              <div ref={trendButtonRef}>
                <TrendSelectButton
                  isOpen={isTrendMenuOpen}
                  onClick={() => setIsTrendMenuOpen((isOpen) => !isOpen)}
                >
                  {getTrendSelectorLabel(activeGraphOption)}
                </TrendSelectButton>
              </div>
            </div>
          }
        />
        {trendMenu}
        {trades.length > 0 && (
          <div className="min-h-0 w-full max-w-full flex-1">
            {GRAPH_FILTERS.map((g, i) => {
              if (activeGraph !== i) return null;

              if (g.graphType === GraphType.Trend) {
                return (
                  <BarChartTrends
                    key={g.name}
                    tradesByDate={tradesByDate}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataKey!}
                    valueType={g.valueType}
                  />
                );
              }

              if (g.graphType === GraphType.ExpiryTrend) {
                return (
                  <BarChartExpiryTrends
                    key={g.name}
                    tradesByExpiry={tradesByExpiry}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataType!}
                    valueType={g.valueType}
                  />
                );
              }

              if (g.graphType === GraphType.StrikeTrend) {
                return (
                  <BarChartStrikeTrends
                    key={g.name}
                    tradesByStrike={tradesByStrike}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataType!}
                    valueType={g.valueType}
                  />
                );
              }

              return null;
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default BarChartSection;
